import axios, { AxiosInstance, AxiosError } from 'axios';

// API client with cookie-based authentication
// Constitution Principle 1: Decoupled frontend/backend communication

class ApiClient {
  private client: AxiosInstance;
  private isRefreshing = false;
  private refreshPromise: Promise<void> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Enable sending cookies with requests
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor: inject guest Bearer token when no Cognito session exists
    this.client.interceptors.request.use(
      (config) => {
        const userEmail = localStorage.getItem('userEmail');
        if (!userEmail) {
          const guestToken = localStorage.getItem('pepoll_guest_token');
          if (guestToken) {
            config.headers = config.headers || {};
            config.headers['Authorization'] = `Bearer ${guestToken}`;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor: Handle errors
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response) {
          // API error response
          const { status, data } = error.response;
          const originalRequest = error.config as (typeof error.config & {
            _retry?: boolean;
          });

          if (
            status === 401 &&
            originalRequest &&
            !originalRequest._retry &&
            !originalRequest.url?.includes('/api/auth/refresh')
          ) {
            originalRequest._retry = true;

            // Guest users have no Cognito session to refresh — don't redirect to login
            const userEmail = localStorage.getItem('userEmail');
            if (!userEmail) {
              return Promise.reject(error);
            }

            try {
              await this.refreshAccessToken();
              return this.client(originalRequest);
            } catch (refreshError) {
              localStorage.removeItem('userEmail');
              localStorage.removeItem('user');
              window.location.href = '/login';
              return Promise.reject(refreshError);
            }
          }

          console.error('API Error:', {
            status,
            data,
            correlationId: (data as any)?.correlationId,
          });
        } else if (error.request) {
          // Network error
          console.error('Network Error:', error.message);
        }

        return Promise.reject(error);
      }
    );
  }

  private async refreshAccessToken(): Promise<void> {
    const userEmail = localStorage.getItem('userEmail');

    if (!userEmail) {
      throw new Error('No user email available for token refresh');
    }

    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = axios
      .post(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/api/auth/refresh`,
        { email: userEmail },
        {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' },
        }
      )
      .then(() => undefined)
      .finally(() => {
        this.isRefreshing = false;
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  getInstance(): AxiosInstance {
    return this.client;
  }
}

export const apiClient = new ApiClient().getInstance();
export default apiClient;
